import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../../api.ts';
import { formatEuros } from '../../../format.ts';
import { fmtDay, toCents, type BillingState, type Client, type ExtraInvoice, type Overview } from './types.ts';

const ERRORS: Record<string, string> = {
  BILLING_INCOMPLETE: 'Complétez d’abord votre profil de facturation.',
  CLIENT_NOT_FOUND: 'Choisissez un client.',
  INVALID_LINES: 'Vérifiez les lignes : désignation, quantité et prix unitaire positifs.',
  INVALID_DATE: 'Date invalide.',
  INVALID_PAYMENT_TERM: 'Délai de paiement invalide (0 à 60 jours).',
  INVALID_VAT_RATE: 'Taux de TVA invalide.',
  INVALID_METHOD: 'Mode de paiement invalide.',
  ALREADY_PAID: 'Cette facture est déjà réglée.',
  ALREADY_CREDITED: 'Cette facture a déjà été annulée par un avoir.',
  CREDIT_NOT_ALLOWED: 'Cette facture ne peut pas être annulée ici.',
  INVALID_REASON: 'Indiquez le motif de l’avoir (3 à 300 caractères).',
};

const METHODS = [
  ['VIREMENT', 'Virement'],
  ['CHEQUE', 'Chèque'],
  ['ESPECES', 'Espèces'],
  ['CARTE', 'Carte bancaire'],
  ['AUTRE', 'Autre'],
];

const today = () => new Date().toISOString().slice(0, 10);

interface Line { description: string; quantity: string; unitPrice: string }
const EMPTY_LINE: Line = { description: '', quantity: '1', unitPrice: '' };

function NewInvoice({ clients, billing, onCreated, onCancel }: { clients: Client[]; billing: BillingState; onCreated: () => void; onCancel: () => void }) {
  const [clientId, setClientId] = useState('');
  const [serviceDate, setServiceDate] = useState(today());
  const [terms, setTerms] = useState('30');
  const [vat, setVat] = useState('20');
  const [lines, setLines] = useState<Line[]>([{ ...EMPTY_LINE }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const liable = billing.profile.vatRegime === 'LIABLE';
  const subtotal = lines.reduce((s, l) => s + Math.round(Number(l.quantity.replace(',', '.')) * toCents(l.unitPrice || '0')) || 0, 0);
  const vatCents = liable ? Math.round((subtotal * Number(vat)) / 100) : 0;
  const setLine = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/extra/invoices', {
        clientId,
        serviceDate,
        paymentTermDays: Number(terms),
        vatRatePct: liable ? Number(vat) : undefined,
        lines: lines.map((l) => ({ description: l.description, quantity: Number(l.quantity.replace(',', '.')), unitPriceCents: toCents(l.unitPrice) })),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Création impossible.' : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 800 }}>Nouvelle facture</div>
      {error && <div className="error-banner">{error}</div>}
      <select className="input" required value={clientId} onChange={(e) => setClientId(e.target.value)}>
        <option value="">Client…</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {clients.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Ajoutez d'abord un client dans l'onglet « Clients ».</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label className="field-label" htmlFor="service-date">Date de la prestation</label>
          <input id="service-date" className="input" type="date" required max={today()} value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} />
        </div>
        <div>
          <label className="field-label" htmlFor="terms">Paiement sous (jours)</label>
          <input id="terms" className="input" type="number" min={0} max={60} value={terms} onChange={(e) => setTerms(e.target.value)} />
        </div>
      </div>
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 100px', gap: 8 }}>
          <input className="input" required placeholder="Désignation" value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} style={{ gridColumn: '1 / -1' }} />
          <input className="input" required inputMode="decimal" placeholder="Qté" aria-label="Quantité" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
          <input className="input" required inputMode="decimal" placeholder="Prix unitaire HT (€)" aria-label="Prix unitaire HT" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} style={{ gridColumn: 'span 2' }} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])}>Ajouter une ligne</button>
        {lines.length > 1 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setLines((ls) => ls.slice(0, -1))}>Retirer la dernière</button>}
      </div>
      {liable && (
        <select className="input" value={vat} onChange={(e) => setVat(e.target.value)} aria-label="Taux de TVA">
          {['20', '10', '5.5', '2.1', '0'].map((r) => <option key={r} value={r}>TVA {r.replace('.', ',')} %</option>)}
        </select>
      )}
      <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
        <span>Total HT : {formatEuros(subtotal)}</span>
        {liable ? <span>TVA : {formatEuros(vatCents)}</span> : <span style={{ color: 'var(--text-tertiary)' }}>TVA non applicable, art. 293 B du CGI</span>}
        <strong>Total à payer : {formatEuros(subtotal + vatCents)}</strong>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" disabled={busy || !clientId}>Émettre la facture</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Annuler</button>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>Une facture émise ne peut plus être modifiée ni supprimée. Sa numérotation est continue.</div>
    </form>
  );
}

function CreditForm({ invoice, onDone, onCancel }: { invoice: ExtraInvoice; onDone: () => void; onCancel: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/extra/invoices/${invoice.id}/credit`, { reason });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Création impossible.' : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        Un avoir annule la facture en totalité. {invoice.status === 'PAID' ? 'Elle était encaissée : le remboursement vient en déduction de votre chiffre d\u2019affaires à déclarer. ' : ''}
        Il est numéroté et définitif ; vous pourrez ensuite émettre une nouvelle facture corrigée.
      </div>
      {error && <div className="error-banner">{error}</div>}
      <input className="input" required minLength={3} maxLength={300} placeholder="Motif (ex. erreur de montant)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-danger-outline btn-sm" disabled={busy}>Émettre l'avoir</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  );
}

function PayForm({ invoice, onDone }: { invoice: ExtraInvoice; onDone: () => void }) {
  const [paidAt, setPaidAt] = useState(today());
  const [method, setMethod] = useState('VIREMENT');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/extra/invoices/${invoice.id}/paid`, { paidAt, method });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Le chiffre d'affaires est compté à la date d'encaissement.</div>
      {error && <div className="error-banner">{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <input className="input" type="date" required max={today()} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} aria-label="Date d'encaissement" />
        <select className="input" value={method} onChange={(e) => setMethod(e.target.value)} aria-label="Mode de paiement">
          {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy}>Enregistrer le paiement</button>
    </form>
  );
}

export function Invoices() {
  const [invoices, setInvoices] = useState<ExtraInvoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [pending, setPending] = useState(0);
  const [creating, setCreating] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [creditingId, setCreditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ invoices: ExtraInvoice[] }>('/extra/invoices').then((r) => setInvoices(r.invoices));
    api.get<{ clients: Client[] }>('/extra/clients').then((r) => setClients(r.clients));
    api.get<BillingState>('/extra/billing').then(setBilling);
    api.get<Overview>('/extra/overview').then((o) => setPending(o.billing.pendingInvoices));
  }, []);
  useEffect(load, [load]);

  async function issuePending() {
    const res = await api.post<{ issued: number }>('/extra/invoices/issue-pending');
    setMessage(res.issued > 0 ? `${res.issued} facture(s) émise(s).` : 'Rien à émettre : complétez votre profil et acceptez le mandat.');
    load();
  }

  if (!billing) return <div className="empty-state">Chargement…</div>;
  const ready = billing.missing.length === 0;

  return (
    <>
      {message && <div className="badge badge-available" style={{ padding: '10px 14px' }}>{message}</div>}
      {!ready && (
        <div className="badge badge-pending" style={{ padding: '10px 14px', fontSize: 12.5, lineHeight: 1.5 }}>
          Complétez votre <Link to="/extra/activite/facturation">profil de facturation</Link> pour établir des factures.
        </div>
      )}
      {pending > 0 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontWeight: 800 }}>{pending} mission(s) payée(s) sans facture</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Mon Extra les facture à votre nom dès que votre profil est complet et le mandat accepté.
          </div>
          <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={issuePending}>Émettre maintenant</button>
        </div>
      )}

      {!creating && ready && <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setCreating(true)}>Facture hors app</button>}
      {creating && <NewInvoice clients={clients} billing={billing} onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}

      {invoices.length === 0 && !creating && <div className="empty-state">Aucune facture pour l'instant.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {invoices.map((inv) => (
          <div key={inv.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800 }}>{inv.number}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {inv.client.name} · {fmtDay(inv.issuedAt)} · {inv.source === 'IN_APP' ? 'Mission Mon Extra' : 'Hors app'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800 }}>{formatEuros(inv.totalCents)}</div>
                {inv.kind === 'CREDIT_NOTE' ? (
                  <span className="badge badge-neutral">Avoir sur {inv.creditedNumber}</span>
                ) : inv.creditNoteNumber ? (
                  <span className="badge badge-unavailable">Annulée · {inv.creditNoteNumber}</span>
                ) : (
                  <span className={`badge ${inv.status === 'PAID' ? 'badge-available' : 'badge-pending'}`}>
                    {inv.status === 'PAID' ? `Payée le ${inv.paidAt ? fmtDay(inv.paidAt) : ''}` : inv.dueDate ? `À régler avant le ${fmtDay(inv.dueDate)}` : 'À régler'}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to={`/extra/factures/${inv.id}`} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }}>Voir / imprimer</Link>
              {inv.kind === 'INVOICE' && !inv.creditNoteNumber && inv.status === 'ISSUED' && payingId !== inv.id && creditingId !== inv.id && <button className="btn btn-primary btn-sm" onClick={() => setPayingId(inv.id)}>Marquer payée</button>}
              {inv.kind === 'INVOICE' && inv.source === 'OFF_APP' && !inv.creditNoteNumber && creditingId !== inv.id && payingId !== inv.id && (
                <button className="btn btn-ghost btn-sm" onClick={() => setCreditingId(inv.id)}>Annuler par un avoir</button>
              )}
            </div>
            {creditingId === inv.id && <CreditForm invoice={inv} onCancel={() => setCreditingId(null)} onDone={() => { setCreditingId(null); load(); }} />}
            {payingId === inv.id && <PayForm invoice={inv} onDone={() => { setPayingId(null); load(); }} />}
          </div>
        ))}
      </div>
    </>
  );
}
